import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, CheckSquare, Star, Key, Settings } from 'lucide-react';
import { useNoteStore } from '../stores/noteStore';
import { useSettingsStore, fontFamilyMap } from '../stores/settingsStore';
import { Note } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import SettingsPanel from './SettingsPanel';

const NoteList: React.FC = () => {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const {
    notes,
    currentNote,
    searchQuery,
    selectedFolder,
    selectedTags,
    folders,
    setCurrentNote,
    setSearchQuery,
  } = useNoteStore();

  const { 
    paperStyle, 
    fontSize, 
    fontFamily, 
    lineOpacity, 
    marginLineOpacity,
    lineSpacing,
  } = useSettingsStore();

  const filteredNotes = useMemo(() => {
    let filtered = notes;

    // Filter by folder
    if (selectedFolder) {
      filtered = filtered.filter(note => note.folderId === selectedFolder);
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(note =>
        selectedTags.every(tag => note.tags.includes(tag))
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(note =>
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query) ||
        note.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort: pinned first, then by updated date
    return filtered.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [notes, selectedFolder, selectedTags, searchQuery]);

  const handleNoteSelect = (note: Note) => {
    setCurrentNote(note);
    // On mobile, navigate to note view
    if (window.innerWidth < 768) {
      navigate(`/note/${note.id}`);
    }
  };

  const currentFolderName = selectedFolder 
    ? folders.find(f => f.id === selectedFolder)?.name || 'Folder'
    : null;

  // Calculate paper background styles
  const opacityToHex = (opacity: number) => {
    const alpha = Math.round((opacity / 100) * 255);
    return alpha.toString(16).padStart(2, '0');
  };
  const lineColor = `#6B7280${opacityToHex(lineOpacity)}`;
  const marginColor = `#DC2626${opacityToHex(marginLineOpacity)}`;
  
  const paperBackground = paperStyle === 'lined' 
    ? {
        backgroundColor: '#FEF9C3',
        backgroundImage: `linear-gradient(to right, transparent 0, transparent 48px, ${marginColor} 48px, ${marginColor} 50px, transparent 50px),
          repeating-linear-gradient(to bottom, transparent 0px, transparent ${lineSpacing - 1}px, ${lineColor} ${lineSpacing - 1}px, ${lineColor} ${lineSpacing}px)`,
        backgroundSize: `100% 100%, 100% ${lineSpacing}px`,
      }
    : { backgroundColor: '#FEF9C3' };

  const getNoteIcon = (note: Note) => {
    if (note.type === 'password') return <Key className="w-4 h-4 text-green-600" />;
    if (note.type === 'checklist') return <CheckSquare className="w-4 h-4 text-blue-600" />;
    return <FileText className="w-4 h-4 text-yellow-700" />;
  };

  const getTimeAgo = (date: Date | string | any) => {
    try {
      // Handle various date formats
      let dateObj: Date;
      if (!date) return '';
      if (date instanceof Date) {
        dateObj = date;
      } else if (date?.toDate) {
        // Firestore Timestamp
        dateObj = date.toDate();
      } else if (date?.seconds) {
        // Firestore Timestamp object
        dateObj = new Date(date.seconds * 1000);
      } else {
        dateObj = new Date(date);
      }
      
      // Check if valid date
      if (isNaN(dateObj.getTime())) return '';
      
      return formatDistanceToNow(dateObj, { addSuffix: false })
        .replace('about ', '')
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' months', 'mo')
        .replace(' month', 'mo')
        .replace('less than a minute', 'now');
    } catch {
      return '';
    }
  };

  return (
    <div className="h-full flex flex-col" style={paperBackground}>
      {/* Header */}
      <div 
        className="p-4 border-b border-gray-200/50"
        style={{ 
          paddingLeft: paperStyle === 'lined' ? '60px' : '16px',
          lineHeight: `${lineSpacing}px`,
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 
              className="font-semibold text-notepad-text"
              style={{ 
                fontFamily: fontFamilyMap[fontFamily],
                fontSize: `${fontSize + 2}px`,
              }}
            >
              {searchQuery ? 'Search Results' : 
               currentFolderName ? currentFolderName : 
               selectedTags.length > 0 ? 'Tagged Notes' : 
               'All Notes'}
            </h2>
            <p className="text-sm text-gray-500">
              {filteredNotes.length} {filteredNotes.length === 1 ? 'note' : 'notes'}
            </p>
          </div>
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-yellow-200/50 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Mobile Search */}
        <div className="mt-3 md:hidden">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notes..."
            className="w-full px-4 py-2 bg-white/70 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            style={{ fontFamily: fontFamilyMap[fontFamily] }}
          />
        </div>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="popLayout">
          {filteredNotes.length === 0 ? (
            <motion.div
              key="empty-state"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-8 text-center"
              style={{ paddingLeft: paperStyle === 'lined' ? '60px' : '32px' }}
            >
              <div className="w-16 h-16 bg-white/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p 
                className="font-medium text-gray-700 mb-2"
                style={{ fontFamily: fontFamilyMap[fontFamily], fontSize: `${fontSize}px` }}
              >
                No notes yet
              </p>
              <p className="text-sm text-gray-500">
                {searchQuery ? 'Try a different search term' : 'Tap the + button to create your first note'}
              </p>
            </motion.div>
          ) : (
            <div>
              {filteredNotes.map((note, index) => (
                <motion.div
                  key={note.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={() => handleNoteSelect(note)}
                  className={`cursor-pointer transition-all duration-150 active:bg-yellow-200/50 ${
                    currentNote?.id === note.id 
                      ? 'bg-yellow-200/60' 
                      : 'hover:bg-yellow-100/50'
                  }`}
                  style={{
                    height: `${lineSpacing}px`,
                    lineHeight: `${lineSpacing}px`,
                    paddingLeft: paperStyle === 'lined' ? '60px' : '16px',
                    paddingRight: '16px',
                  }}
                >
                  <div className="flex items-center gap-2 h-full">
                    {/* Icon */}
                    <span className="flex-shrink-0">
                      {getNoteIcon(note)}
                    </span>
                    
                    {/* Pinned indicator */}
                    {note.isPinned && (
                      <Star className="w-3 h-3 text-yellow-500 flex-shrink-0" fill="currentColor" />
                    )}
                    
                    {/* Locked indicator */}
                    {note.isLocked && (
                      <span className="text-xs">🔒</span>
                    )}
                    
                    {/* Title */}
                    <span 
                      className="flex-1 truncate text-notepad-text"
                      style={{ 
                        fontFamily: fontFamilyMap[fontFamily],
                        fontSize: `${fontSize}px`,
                      }}
                    >
                      {note.title || 'Untitled'}
                    </span>
                    
                    {/* Time ago */}
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {getTimeAgo(note.updatedAt)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
      </AnimatePresence>
    </div>
  );
};

export default NoteList;
